import { Color, Container, Graphics } from 'pixi.js';

export class PlatformWithRainbow extends Container {
    constructor(platformGraphics, thickness = 5) {
        super();
        this.platform = platformGraphics;
        this.rainbow = new Graphics();
        this.thickness = thickness;

        this.addChild(this.rainbow);
        this.addChild(this.platform);

        this.hue = 10;
    }

    updateRainbow() {
        const bounds = this.platform.getBounds();
        this.rainbow.clear();

        // Draw the rainbow background
        const fillColor = Color.fromHSL(this.hue / 360, 1, 0.5);
        this.rainbow.rect(0, 0, bounds.width, bounds.height);
        this.rainbow.fill({ color: fillColor.toNumber() });

        // Draw the rainbow border
        const lineColor = Color.fromHSL(((this.hue + 180) % 360) / 360, 1, 0.5);
        this.rainbow.rect(0, 0, bounds.width, bounds.height);
        this.rainbow.stroke({ width: this.thickness, color: lineColor.toNumber(), alpha: 1 });

        this.hue = (this.hue + 1) % 360;
    }
}

export class PlatformWithShadow extends Container {
    private impactAnimationTime: number = 0;
    private impactIntensity: number = 0;
    private originalScale: { x: number; y: number } = { x: 1, y: 1 };

    constructor(platformGraphics, shadowGraphics) {
        super();
        this.shadow = shadowGraphics;
        this.platform = platformGraphics;

        this.addChild(this.shadow);
        this.addChild(this.platform);

        // Initial shadow offset
        this.shadowOffset = { x: 0, y: 0 };
        this.shadow.position.set(this.shadowOffset.x, this.shadowOffset.y);

        // Maximum shadow offset
        this.maxOffset = 8;
        
        // Store original scale
        this.originalScale = { x: this.scale.x, y: this.scale.y };
    }

    updateShadow(ballX, ballY, yOffset) {
        const platformBounds = this.platform.getBounds();
        // Calculate the center of the platform
        const platformCenterX = platformBounds.x + platformBounds.width / 2;
        const platformCenterY = platformBounds.y + platformBounds.height / 2 + yOffset;

        // Calculate the angle and distance from the platform center to the ball
        const dx = ballX - platformCenterX;
        const dy = ballY - platformCenterY;
        const angle = Math.atan2(dy, dx);
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Calculate the shadow offset
        const maxOffset = this.maxOffset;
        const offsetMagnitude = Math.min(distance / 10, maxOffset);
        this.shadowOffset.x = -(offsetMagnitude * Math.cos(angle));
        this.shadowOffset.y = -(offsetMagnitude * Math.sin(angle));

        // Apply the offset to the shadow
        this.shadow.position.set(this.shadowOffset.x, this.shadowOffset.y);
    }

    triggerImpact(intensity: number = 1) {
        this.impactIntensity = Math.min(intensity, 2);
        this.impactAnimationTime = 300; // 300ms animation
    }

    updateImpact(delta: number) {
        if (this.impactAnimationTime > 0) {
            this.impactAnimationTime -= delta * 60; // Convert delta to ms
            
            const progress = this.impactAnimationTime / 300;
            const easedProgress = 1 - (1 - progress) * (1 - progress); // Ease out
            
            // Squash and stretch effect - reduced to 5% of original
            const squashAmount = this.impactIntensity * 0.0075 * (1 - easedProgress); // 0.15 * 0.05 = 0.0075
            const stretchAmount = this.impactIntensity * 0.005 * (1 - easedProgress); // 0.1 * 0.05 = 0.005
            
            // Squash vertically, stretch horizontally
            this.scale.y = this.originalScale.y * (1 - squashAmount);
            this.scale.x = this.originalScale.x * (1 + stretchAmount);
            
            if (this.impactAnimationTime <= 0) {
                this.scale.x = this.originalScale.x;
                this.scale.y = this.originalScale.y;
                this.impactAnimationTime = 0;
            }
        }
    }
}